import { ReactNode, useMemo, useState } from 'react';

import { useCorporationSettings } from '@commenda/api/new-sales-tax';
import {
  TIndirectTaxTransactionDetail,
  TIndirectTaxTransactionRegion,
} from '../types';
import { IndirectTaxTransactionDetailLineItemsTable } from './IndirectTaxTransactionDetailLineItemsTable';
import { IndirectTaxTransactionDetailSummary } from './IndirectTaxTransactionDetailSummary';
import { IndirectTaxTransactionLineItemSheet } from './IndirectTaxTransactionLineItemSheet';

interface IndirectTaxTransactionDetailPageProps {
  transaction: TIndirectTaxTransactionDetail;
  region: TIndirectTaxTransactionRegion;
  header: ReactNode;
  onViewProduct?: (productId: string) => void;
  onViewCustomer?: (customerId: string) => void;
  onViewRegistration?: (registrationId: string) => void;
  onGenerateTaxBreakdown?: () => Promise<void>;
  isGeneratingTaxBreakdown?: boolean;
  businessEntityId: number;
}

const IndirectTaxTransactionDetailPage = ({
  transaction,
  region,
  header,
  onViewProduct,
  onViewCustomer,
  onViewRegistration,
  onGenerateTaxBreakdown,
  isGeneratingTaxBreakdown,
  businessEntityId,
}: IndirectTaxTransactionDetailPageProps) => {
  const [selectedLineItemId, setSelectedLineItemId] = useState<string | null>(
    null
  );

  const selectedLineItem = useMemo(
    () =>
      transaction.lineItems.find((item) => item.id === selectedLineItemId) ??
      null,
    [transaction.lineItems, selectedLineItemId]
  );

  const { data: corporationSettings } = useCorporationSettings({
    corporationId: businessEntityId,
  });

  const defaultProductTaxCode = useMemo(() => {
    const countrywideSetting = corporationSettings?.data?.find(
      (setting) => setting?.state === 'COUNTRYWIDE'
    );
    // Safe: SettingsData.data is typed but we need to access dynamic keys
    const settingData = countrywideSetting?.data as unknown as
      | Record<string, unknown>
      | undefined;
    return (
      (settingData?.default_product_taxability_code as string) ?? undefined
    );
  }, [corporationSettings]);

  return (
    <>
      <div className="p-3xl border-b">{header}</div>

      <IndirectTaxTransactionDetailSummary
        transaction={transaction}
        region={region}
      />

      <IndirectTaxTransactionDetailLineItemsTable
        lineItems={transaction.lineItems}
        region={region}
        onRowClick={(lineItem) => setSelectedLineItemId(lineItem.id)}
        onViewProduct={onViewProduct}
        defaultValues={{ productTaxCode: defaultProductTaxCode }}
      />

      <IndirectTaxTransactionLineItemSheet
        lineItem={selectedLineItem}
        isOpen={selectedLineItemId !== null}
        onClose={() => setSelectedLineItemId(null)}
        currency={transaction.currency}
        defaultValues={{ productTaxCode: defaultProductTaxCode }}
        onViewRegistration={onViewRegistration}
        onViewCustomer={onViewCustomer}
        onViewProduct={onViewProduct}
        onGenerateTaxBreakdown={onGenerateTaxBreakdown}
        isGeneratingTaxBreakdown={isGeneratingTaxBreakdown}
        destinationCountryCode={transaction.destinationCountryCode}
        transactionType={transaction.transactionType}
      />
    </>
  );
};

export { IndirectTaxTransactionDetailPage };
